import pushRoute from '@app/utils/routing';
import {
  Box,
  Icons,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { LtiProvider } from '@ublend-npm/aula-schema';
import React from 'react';
import { MANUAL } from '@core/assignments/constants';
import { ResponsiveAssignment } from '@core/assignments/types';
import formatDate from '@core/utils/formatDate';
import { createStyles } from './AssignmentCard.styles';
import AnonymousIcon from './AnonymousIcon';
import Logo from './Logo';
import { getStatus, getStatusLabel, STATUS } from './utils';

type Props = {
  assignment: ResponsiveAssignment;
  spaceId: string;
  providers: Array<LtiProvider>;
  onMaterialClick?: (materialId: string, materialTitle?: string) => void;
};

const AssignmentCard = ({
  assignment,
  spaceId,
  providers,
  onMaterialClick,
}: Props) => {
  const theme = useTheme();
  const isMdBelow = useMediaQuery(theme.breakpoints.down('md'));
  const styles = createStyles(theme);
  const assignmentId = assignment.id;
  const assignmentRoute = `/dashboard/${spaceId}/journey/assignments/${assignmentId}`;
  const linkedMaterialId = assignment.linkedMaterials?.[0]?.id;

  const linkedMaterials = assignment.linkedMaterials || [];
  const firstMaterial = linkedMaterials[0];
  const additionalMaterialsCount = linkedMaterials.length - 1;

  const handleMaterialClick = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.stopPropagation();
    if (linkedMaterialId) {
      onMaterialClick?.(
        linkedMaterialId,
        assignment.linkedMaterials?.[0]?.title
      );
    }
  };

  const handleMaterialKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return null;
    }
    event.preventDefault();
    if (linkedMaterialId) {
      onMaterialClick?.(
        linkedMaterialId,
        assignment.linkedMaterials?.[0]?.title
      );
    }
  };

  const onClickAssignment = () => {
    pushRoute(assignmentRoute);
  };

  const assignmentStatus =
    assignment.provider === MANUAL
      ? STATUS.manualAssignment
      : getStatus({
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          status: assignment.status,
          shouldCloseOnDueDate: assignment.shouldCloseOnDueDate,
        });

  let statusStyle = styles.status;

  if (assignmentStatus === STATUS.pastDueDate) {
    statusStyle = styles.pastDueDate;
  } else if (assignmentStatus === STATUS.manualAssignment) {
    statusStyle = styles.manualStatus;
  }

  return (
    <Box onClick={onClickAssignment} sx={styles.cardWrapper}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardDetails}>
          <Logo assignment={assignment} providers={providers} />
          {assignmentStatus && (
            <Box sx={statusStyle}>{getStatusLabel(assignmentStatus)}</Box>
          )}
          {assignment.isSubmitted && (
            <Box sx={styles.submittedStatus}>Submitted</Box>
          )}
          {!!assignment.groupSet && (
            <Tooltip
              title="This is a group assignment"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box sx={styles.headerIcon}>
                <Icons.GroupOutlined />
              </Box>
            </Tooltip>
          )}
          {assignment.isAnonymised && (
            <Tooltip
              title="This assignment is anonymised"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box sx={styles.headerIcon}>
                <AnonymousIcon />
              </Box>
            </Tooltip>
          )}
          {assignment.isHidden && (
            <Tooltip
              title="This assignment is hidden from students"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box sx={styles.headerIcon}>
                <Icons.VisibilityOffOutlined />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box sx={styles.menuIcon}>
          <Icons.MoreVert />
        </Box>
      </Box>
      <Box sx={styles.title}>{assignment.title}</Box>
      {assignment.extensions && (
        <Box sx={styles.extensions}>
          <Icons.ExtensionOutlined sx={styles.extensionsIcon} />
          <span>{assignment.extensions.length} extensions available</span>
        </Box>
      )}
      {firstMaterial && (
        <Box
          sx={styles.materials}
          onClick={handleMaterialClick}
          role="button"
          tabIndex={0}
          onKeyDown={handleMaterialKeyDown}
        >
          <Icons.BookOutlined sx={styles.materialsIcon} />
          <Box sx={styles.materialsTitle}>{firstMaterial.title}</Box>
          {additionalMaterialsCount > 0 && (
            <Box sx={styles.materialsCount}>+ {additionalMaterialsCount}</Box>
          )}
        </Box>
      )}
      {assignment.endDate && (
        <Box sx={styles.endDate}>Due: {formatDate(assignment.endDate)}</Box>
      )}
    </Box>
  );
};

export default AssignmentCard;
